import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap rounded-control text-sm font-semibold tracking-[-0.01em]',
    'ring-offset-background active:scale-[.97]',
    '[transition:background_var(--motion-base),box-shadow_var(--motion-base),transform_var(--motion-fast)]',
    'motion-reduce:[transition:none]',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-muted-foreground disabled:shadow-none disabled:border-transparent disabled:active:scale-100',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'btn-primary-gradient text-white shadow-[0_1px_2px_rgba(15,23,42,.08),0_2px_8px_-2px_rgba(79,70,229,.35),inset_0_1px_0_rgba(255,255,255,.16)] hover:shadow-[0_2px_4px_rgba(15,23,42,.12),0_4px_14px_-2px_rgba(79,70,229,.45),inset_0_1px_0_rgba(255,255,255,.16)]',
        secondary:
          'border border-border-strong bg-white text-foreground shadow-sm hover:border-slate-400 hover:bg-surface-2 dark:bg-card dark:hover:bg-surface-2',
        tonal: 'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-500/15 dark:text-brand-300 dark:hover:bg-brand-500/25',
        ghost: 'text-foreground hover:bg-brand-600/7 hover:text-brand-700 dark:hover:text-brand-300',
        danger: 'bg-destructive text-white shadow-sm hover:bg-[color-mix(in_srgb,var(--color-destructive)_85%,black)]',
      },
      size: {
        default: 'h-(--size-control-md) px-5',
        sm: 'h-(--size-control-sm) px-3.5',
        lg: 'h-(--size-control-lg) px-7 text-[15px]',
        icon: 'h-(--size-control-md) w-(--size-control-md)',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner in place of `icon` and blocks interaction, without the greyed-out disabled look. */
  loading?: boolean;
  /** Optional leading icon (16px). Replaced by the loading spinner while `loading` is true. */
  icon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, icon, disabled, children, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLButtonElement | null>(null);
    const lastWidthRef = React.useRef<number | undefined>(undefined);

    React.useLayoutEffect(() => {
      if (!loading && innerRef.current) {
        lastWidthRef.current = innerRef.current.offsetWidth;
      }
    });

    const setRef = (node: HTMLButtonElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    // asChild renders a single child (e.g. a router Link) via Radix Slot, which
    // requires exactly one element — the icon/loading slot only applies to real buttons.
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={setRef}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        style={loading && lastWidthRef.current ? { minWidth: lastWidthRef.current } : undefined}
        ref={setRef}
        disabled={disabled}
        aria-busy={loading || undefined}
        aria-disabled={loading || disabled || undefined}
        {...props}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          icon && <span className="mr-2 flex h-4 w-4 shrink-0 items-center [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
